var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { menuHandler } from "@/modules/menu";
import { mobileNavigation } from "@/modules/mobile-navigation";
import { channelSelector } from "@/modules/channel-selector";
import { headerCoordinator } from "@/modules/header-coordinator";
import { legalDisclaimer, utils } from "@/modules/legal-disclaimer/legalDisclaimer";
console.log("🚀 Global Module Loader v4.0 - Starting initialization...");
const startTime = performance.now();
console.log("🔧 Module loader starting - jQuery available:", typeof $, (_a = $.fn) === null || _a === void 0 ? void 0 : _a.jquery);
import { externalLinks } from "@/modules/external-link/externalLinks";
import { backToTop } from "@/modules/backtotop/backtotop";
import ExpandingText from "@/modules/expanding-text/expanding-text";
import { header } from "@/modules/header";
import { smoothScroll } from "@/modules/smooth-scroll";
import { loaderFeature } from "@/modules/loading";
import { Details } from "@/modules/details";
console.log("✅ Essential UI modules imported");
import { fadeIn, stopPropagation, scrollContent, removeClasses, toggleMultipleCheckboxes, checkboxValueCookie, } from "@/modules/feature";
console.log("✅ Core feature modules imported");
import Toggler from "@/modules/toggler/toggler";
import StickyNav from "@/modules/sticky-nav/index";
import EnhancedStickyNav from "@/modules/sticky-nav/enhanced-sticky-nav";
import HelloBar from "@/modules/hello-bar/index";
import { scrollToURLFragment } from "@/modules/url-fragment-scroll";
console.log("✅ Navigation modules imported");
let stickyNav;
let enhancedStickyNav;
let helloBar;
let regulatoryBanner;
const moduleLoadTimes = {};
const loadedModules = new Set();
const logModuleLoad = (moduleName, startTime, success = true) => {
    const duration = performance.now() - startTime;
    moduleLoadTimes[moduleName] = duration;
    if (success) {
        loadedModules.add(moduleName);
        console.log(`✅ ${moduleName}: Loaded and initialized (${duration.toFixed(2)}ms)`);
    }
    else {
        console.warn(`❌ ${moduleName}: Failed to load (${duration.toFixed(2)}ms)`);
    }
};
const handleModuleError = (moduleName, error, critical = false) => {
    console.error(`${critical ? '🚨' : '⚠️'} ${moduleName} failed:`, error);
    if (critical) {
        console.error(`Critical module ${moduleName} failure may impact site functionality`);
    }
};
const waitForCookieProBannerClose = (callback, maxWaitTime = 10000) => {
    let callbackExecuted = false;
    const startTime = performance.now();
    const executeCallback = () => {
        if (callbackExecuted)
            return;
        callbackExecuted = true;
        callback();
    };
    const handleOneTrustAvailable = (OneTrust) => {
        console.log('🍪 CookiePro detected - checking banner status');
        if (window.OptanonAlertBoxClosed) {
            console.log('✅ CookiePro consent already given - executing callback immediately');
            executeCallback();
            return;
        }
        const isBannerShown = OneTrust.IsAlertBoxClosed() === false;
        if (isBannerShown) {
            console.log('🍪 CookiePro banner is active - waiting for close event');
            const onBannerClose = () => {
                console.log('✅ CookiePro banner closed - executing callback');
                executeCallback();
            };
            window.addEventListener('OneTrustGroupsUpdated', onBannerClose, { once: true });
            const originalOptanonWrapper = window.OptanonWrapper;
            window.OptanonWrapper = function () {
                if (originalOptanonWrapper && typeof originalOptanonWrapper === 'function') {
                    originalOptanonWrapper();
                }
                onBannerClose();
            };
        }
        else {
            console.log('✅ CookiePro banner not active - executing callback immediately');
            executeCallback();
        }
    };
    const pollForOneTrust = () => {
        const elapsed = performance.now() - startTime;
        const OneTrust = window.OneTrust;
        if (OneTrust) {
            if (typeof OneTrust.IsAlertBoxClosed === 'function') {
                handleOneTrustAvailable(OneTrust);
                return;
            }
            console.log('🍪 OneTrust object found but API not ready yet - continuing to poll');
        }
        if (elapsed >= maxWaitTime) {
            console.log('✅ No CookiePro detected after waiting - executing callback');
            executeCallback();
            return;
        }
        const pollCount = Math.floor(elapsed / 50);
        const nextInterval = Math.min(50 * Math.pow(1.5, pollCount), 500);
        setTimeout(pollForOneTrust, nextInterval);
    };
    try {
        const hasCookieProScript = !!document.querySelector('script[src*="cdn.cookielaw.org"]');
        if (!hasCookieProScript) {
            console.log('✅ No CookiePro script detected - executing callback immediately');
            setTimeout(executeCallback, 0);
            return;
        }
        console.log('🍪 CookiePro script found - waiting for OneTrust to initialise');
        setTimeout(pollForOneTrust, 50);
    }
    catch (error) {
        console.warn('⚠️ Error in CookiePro banner handler:', error);
        executeCallback();
    }
};
const lazyModules = {
    loadDatePicker() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            if (document.querySelector('[data-datepicker-input], .js-date-picker, #datepicker')) {
                try {
                    yield import('jquery-ui/ui/widgets/datepicker');
                    const { datePicker } = yield import("@/modules/jquery-ui/datePicker");
                    datePicker.init();
                    logModuleLoad('jQuery UI DatePicker', startTime);
                    return datePicker;
                }
                catch (error) {
                    handleModuleError('jQuery UI DatePicker', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadAutoSuggest() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            if (document.querySelector('.js-data-all-auto-suggest')) {
                try {
                    yield import('jquery-ui/ui/widgets/autocomplete');
                    const { autoSuggest } = yield import("@/modules/jquery-ui/autoSuggest");
                    autoSuggest.init();
                    logModuleLoad('jQuery UI AutoSuggest', startTime);
                    return autoSuggest;
                }
                catch (error) {
                    handleModuleError('jQuery UI AutoSuggest', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadSelectMenu() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            const selectElements = document.querySelectorAll(".js-select-control");
            if (selectElements.length > 0) {
                try {
                    yield import('jquery-ui/ui/widgets/selectmenu');
                    $(selectElements).selectmenu();
                    logModuleLoad('jQuery UI SelectMenu', startTime);
                    return true;
                }
                catch (error) {
                    handleModuleError('jQuery UI SelectMenu', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadLinkCards() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            const linkCardElements = document.querySelectorAll('.js-link-card');
            if (linkCardElements.length > 0) {
                try {
                    const { default: LinkCard } = yield import("@/modules/link-card/linkCard");
                    linkCardElements.forEach((element) => {
                        new LinkCard(element);
                    });
                    logModuleLoad('Link Cards', startTime);
                    return true;
                }
                catch (error) {
                    handleModuleError('Link Cards', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadCharts() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            if (document.querySelector('.js-chart')) {
                try {
                    const { setupCharts } = yield import("@/modules/charts");
                    setupCharts();
                    logModuleLoad('Charts', startTime);
                    return setupCharts;
                }
                catch (error) {
                    handleModuleError('Charts', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadTippy() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            const tooltips = document.querySelectorAll(".js-tippy");
            if (tooltips.length > 0) {
                try {
                    const { default: tippy } = yield import("tippy.js");
                    tooltips.forEach((tooltip) => {
                        var _a, _b;
                        const message = tooltip.dataset.message;
                        const theme = (_b = (_a = tooltip.dataset) === null || _a === void 0 ? void 0 : _a.theme) !== null && _b !== void 0 ? _b : "dark-blue";
                        tippy(tooltip, {
                            content: message,
                            theme: theme,
                        });
                    });
                    logModuleLoad('Tippy Tooltips', startTime);
                    return tippy;
                }
                catch (error) {
                    handleModuleError('Tippy Tooltips', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadForms() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            if (document.querySelector('.js-contact-form')) {
                try {
                    const { initContactForm } = yield import("@/helpers/form");
                    initContactForm();
                    logModuleLoad('Contact Forms', startTime);
                    return initContactForm;
                }
                catch (error) {
                    handleModuleError('Contact Forms', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadCarousels() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            const carousels = document.querySelectorAll('.js-wide-small-image-carousel, .js-hero-carousel');
            console.log(`🔍 Carousel check: ${carousels.length} elements found`);
            if (carousels.length > 0) {
                try {
                    console.log("🔄 Loading carousel modules...");
                    const [{ carousel }, { flexSliderCarousel }] = yield Promise.all([
                        import("@/modules/carousel"),
                        import("@/modules/flex-slider-carousel")
                    ]);
                    carousel.applyStyles();
                    window["flexSliderCarousel"] = flexSliderCarousel;
                    window["carousel"] = carousel;
                    if (typeof $.fn.flexslider !== 'undefined') {
                        flexSliderCarousel.init();
                        console.log("🎠 FlexSlider initialized successfully");
                    }
                    else {
                        console.warn("⚠️ jQuery flexslider plugin not available");
                    }
                    logModuleLoad('Carousels', startTime);
                    return { carousel, flexSliderCarousel };
                }
                catch (error) {
                    handleModuleError('Carousels', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadHeroCarousel() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            if (document.querySelector('[data-hero-carousel]')) {
                try {
                    const { heroCarousel } = yield import("@/modules/hero-carousel");
                    heroCarousel.init();
                    logModuleLoad('Hero Carousel', startTime);
                    return heroCarousel;
                }
                catch (error) {
                    handleModuleError('Hero Carousel', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadMaps() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            if (document.querySelector('[id^="map-"], .js-map-icon')) {
                try {
                    const { googleMapView } = yield import("@/modules/maps");
                    window["googleMapView"] = googleMapView;
                    logModuleLoad('Google Maps', startTime);
                    return googleMapView;
                }
                catch (error) {
                    handleModuleError('Google Maps', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadFunds() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            if (document.querySelector('#fund-query, #fundUmbrella, .fund-page, #datepicker')) {
                try {
                    const [{ fund }, { tableFunds }] = yield Promise.all([
                        import("@/modules/funds"),
                        import("@/modules/feature")
                    ]);
                    window["fund"] = fund;
                    window["tableFunds"] = tableFunds;
                    logModuleLoad('Investment Funds', startTime);
                    return { fund, tableFunds };
                }
                catch (error) {
                    handleModuleError('Investment Funds', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadModals() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            const modals = document.querySelectorAll('[data-modal-target], [data-micromodal-trigger]');
            if (modals.length > 0) {
                try {
                    const [{ simpleModal }, { microModal }] = yield Promise.all([
                        import("@/modules/feature"),
                        import("@/modules/feature/modal")
                    ]);
                    simpleModal.init();
                    microModal.init();
                    logModuleLoad('Modal System', startTime);
                    return { simpleModal, microModal };
                }
                catch (error) {
                    handleModuleError('Modal System', error);
                    return null;
                }
            }
            return null;
        });
    },
    loadClientModules() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            const promises = [];
            if (document.querySelector('.js-sortable#client-instruction-supertable')) {
                promises.push(import("@/modules/client-instructions").then(module => {
                    const handler = new module.default();
                    handler.init();
                    return { type: 'instructions', handler };
                }).catch(error => {
                    handleModuleError('Client Instructions', error);
                    return null;
                }));
            }
            if (document.querySelector('.js-sortable#client-signatories-supertable')) {
                promises.push(import("@/modules/client-signatories").then(module => {
                    const handler = new module.default();
                    handler.init();
                    return { type: 'signatories', handler };
                }).catch(error => {
                    handleModuleError('Client Signatories', error);
                    return null;
                }));
            }
            if (document.querySelector('#insight-client-kyc')) {
                promises.push(import("@/modules/client-kyc").then(module => {
                    module.ClientKYCHandler.init();
                    return { type: 'kyc', handler: module.ClientKYCHandler };
                }).catch(error => {
                    handleModuleError('Client KYC', error);
                    return null;
                }));
            }
            const mfaResetContainer = document.querySelector('.js-mfa-reset');
            if (mfaResetContainer) {
                const configId = mfaResetContainer.getAttribute('data-config-id');
                const configScript = configId ? document.getElementById(configId) : null;
                if (configScript) {
                    promises.push(import("@/modules/portal-mfa-reset").then(module => {
                        const config = JSON.parse(configScript.textContent || '{}');
                        module.portalMfaReset.init(mfaResetContainer, config);
                        return { type: 'mfa-reset', handler: module.portalMfaReset };
                    }).catch(error => {
                        handleModuleError('Portal MFA Reset', error);
                        return null;
                    }));
                }
            }
            const liquidityCalculatorContainer = document.querySelector('.js-liquidity-calculator');
            if (liquidityCalculatorContainer) {
                const configId = liquidityCalculatorContainer.getAttribute('data-config-id');
                const configScript = configId ? document.getElementById(configId) : null;
                if (configScript) {
                    promises.push(import("@/modules/liquidity-calculator").then(module => {
                        const config = JSON.parse(configScript.textContent || '{}');
                        module.liquidityCalculator.init(liquidityCalculatorContainer, config);
                        return { type: 'liquidity-calculator', handler: module.liquidityCalculator };
                    }).catch(error => {
                        handleModuleError('Liquidity Calculator', error);
                        return null;
                    }));
                }
            }
            if (promises.length > 0) {
                const results = yield Promise.all(promises);
                const successCount = results.filter(r => r !== null).length;
                logModuleLoad(`Client Portal (${successCount}/${promises.length})`, startTime);
                return results;
            }
            return null;
        });
    },
    loadOtherModules() {
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = performance.now();
            const promises = [];
            let loadedCount = 0;
            if (document.querySelector('.js-parallax, .js-parallax-large')) {
                try {
                    const { parallax } = yield import("@/modules/parallax");
                    parallax.init();
                    promises.push(parallax);
                    loadedCount++;
                    console.log("✅ Parallax effects initialized");
                }
                catch (error) {
                    handleModuleError('Parallax Effects', error);
                }
            }
            if (document.querySelector('.js-filters')) {
                try {
                    const { Filters } = yield import("@/modules/filters");
                    Filters.init();
                    promises.push(Filters);
                    loadedCount++;
                    console.log("✅ Content filters initialized");
                }
                catch (error) {
                    handleModuleError('Content Filters', error);
                }
            }
            if (document.querySelector('.js-editor-quote')) {
                try {
                    const { TextEditor } = yield import("@/modules/text-editor");
                    TextEditor.init();
                    promises.push(TextEditor);
                    loadedCount++;
                    console.log("✅ Text editor quotes initialized");
                }
                catch (error) {
                    handleModuleError('Text Editor', error);
                }
            }
            if (document.querySelector('.js-sortable')) {
                try {
                    const { SuperTable } = yield import("@/modules/super-table");
                    SuperTable.init({
                        skipListIds: [
                            'client-instruction-supertable',
                            'client-signatories-supertable'
                        ]
                    });
                    promises.push(SuperTable);
                    loadedCount++;
                    console.log("✅ Advanced tables initialized");
                }
                catch (error) {
                    handleModuleError('Advanced Tables', error);
                }
            }
            if (loadedCount > 0) {
                logModuleLoad(`UI Enhancements (${loadedCount} modules)`, startTime);
                return promises;
            }
            return null;
        });
    }
};
const initializeCriticalFeatures = () => {
    const startTime = performance.now();
    console.log("🔧 Phase 1: Initializing critical features...");
    try {
        fadeIn.init();
        Toggler.init();
        backToTop.init();
        ExpandingText.init();
        scrollContent.init();
        smoothScroll.init();
        loaderFeature.init();
        externalLinks.init();
        removeClasses.init();
        stopPropagation.init();
        checkboxValueCookie.init();
        toggleMultipleCheckboxes.init();
        menuHandler.init();
        mobileNavigation.init({ debug: true });
        headerCoordinator.init();
        if (document.querySelector('[data-channel-selector-button]')) {
            channelSelector.init('[data-channel-selector-button]', '[data-channel-selector-text]', '[data-channel-selector-links]');
        }
        Details.init();
        window["externalLinks"] = externalLinks;
        window["mobileNavigation"] = mobileNavigation;
        const duration = performance.now() - startTime;
        console.log(`✅ Phase 1 complete: Critical features ready (${duration.toFixed(2)}ms)`);
    }
    catch (error) {
        handleModuleError('Critical Features', error, true);
    }
};
const initializeProgressiveFeatures = () => __awaiter(void 0, void 0, void 0, function* () {
    const startTime = performance.now();
    console.log("🔄 Phase 2: Loading progressive features...");
    const loadPromises = [
        lazyModules.loadCharts(),
        lazyModules.loadTippy(),
        lazyModules.loadDatePicker(),
        lazyModules.loadAutoSuggest(),
        lazyModules.loadSelectMenu(),
        lazyModules.loadLinkCards(),
        lazyModules.loadForms(),
        lazyModules.loadCarousels(),
        lazyModules.loadHeroCarousel(),
        lazyModules.loadMaps(),
        lazyModules.loadFunds(),
        lazyModules.loadModals()
    ];
    const results = yield Promise.allSettled(loadPromises);
    const moduleNames = [
        'charts', 'tooltips', 'datePicker', 'autoSuggest', 'selectMenu', 'linkCards',
        'forms', 'carousels', 'heroCarousel', 'maps', 'funds', 'modals'
    ];
    let successCount = 0;
    results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value) {
            successCount++;
        }
        else if (result.status === 'rejected') {
            console.warn(`⚠️ ${moduleNames[index]} failed to load:`, result.reason);
        }
    });
    yield lazyModules.loadClientModules();
    yield lazyModules.loadOtherModules();
    const duration = performance.now() - startTime;
    console.log(`✅ Phase 2 complete: ${successCount}/${loadPromises.length} progressive features loaded (${duration.toFixed(2)}ms)`);
});
const initializeSpecializedFeatures = () => __awaiter(void 0, void 0, void 0, function* () {
    const startTime = performance.now();
    console.log("🎯 Phase 3: Loading specialized features...");
    let loadedCount = 0;
    try {
        const searchElements = document.querySelectorAll('#txtPopUpSearchKey, #search, .js-search-toggle, .search, [data-search]');
        if (searchElements.length > 0) {
            const { mainUsSearch } = yield import("@/modules/search");
            mainUsSearch.init();
            loadedCount++;
            console.log("✅ Search functionality initialized");
        }
        const autoCompletes = document.querySelectorAll(".js-autocomplete");
        if (autoCompletes.length > 0) {
            const { GenericAutoComplete } = yield import("@/modules/autocomplete");
            GenericAutoComplete();
            loadedCount++;
            console.log("✅ Generic autocomplete initialized");
        }
        const contentTabs = document.querySelectorAll(".js-content-tabs");
        if (contentTabs.length > 0) {
            const { default: TabHandler } = yield import("@/modules/tabs");
            contentTabs.forEach((contentTab) => {
                const tabs = Array.from(contentTab.querySelectorAll(".js-content-tab"));
                const panels = Array.from(contentTab.querySelectorAll(".js-content-tab-panel"));
                if (tabs.length > 0 && panels.length > 0) {
                    new TabHandler({ tabs, panels });
                }
            });
            loadedCount++;
            console.log("✅ Content tabs initialized");
        }
        if (document.querySelector('[data-vertical-tabs]')) {
            const { verticalTabs } = yield import("@/modules/vertical-tabs");
            verticalTabs.init();
            loadedCount++;
            console.log("✅ Vertical tabs initialized");
        }
        if (document.querySelector('#insight-ldi')) {
            const { default: LdiTool } = yield import("@/modules/ldi/index");
            LdiTool.init();
            loadedCount++;
            console.log("✅ LDI tool initialized");
        }
        if (document.querySelector('#gmr-hub-filter, .js-gmrhub-results, .js-date-translate')) {
            const { gmrHub } = yield import("@/modules/gmr-hub");
            gmrHub.init();
            globalThis.gmrHub = gmrHub;
            loadedCount++;
            console.log("✅ GMR hub initialized");
        }
        if (document.querySelector('dialog, .js-basic-dialog')) {
            const { setupDialogs } = yield import("@/modules/dialog/index");
            setupDialogs();
            loadedCount++;
            console.log("✅ Dialog system initialized");
        }
        if (document.querySelector('.js-export-disclaimer')) {
            const { showExportDisclaimer } = yield import("@/modules/export-disclaimer/exportDisclaimer");
            window.showExportDisclaimer = showExportDisclaimer;
            loadedCount++;
            console.log("✅ Export disclaimer initialized");
        }
        if (document.querySelector('.js-legal-disclaimer')) {
            try {
                console.log('🔍 Loading legal disclaimer system...');
                window.mainLegalDisclaimer = legalDisclaimer;
                window.legalDisclaimerUtils = utils;
                window.LegalDisclaimerCookieManager = utils.CookieManager;
                window.LegalDisclaimerChannelManager = utils.ChannelManager;
                window.LegalDisclaimerViewCountManager = utils.ViewCountManager;
                console.log('✅ Legal disclaimer system exposed globally');
                waitForCookieProBannerClose(() => {
                    legalDisclaimer.init(document.querySelector('.js-legal-disclaimer'));
                    loadedCount++;
                    console.log("✅ Legal disclaimer system initialized");
                });
            }
            catch (error) {
                console.error("❌ Failed to load legal disclaimer system:", error);
                try {
                    console.log("🔧 Setting up emergency fallback handlers");
                    $(document).off('click.emergencyFallback').on('click.emergencyFallback', '.js-legal-disclaimer_close, .js-legal-disclaimer_button-decline', function (e) {
                        e.preventDefault();
                        console.log('🚨 Emergency fallback: closing disclaimer');
                        const redirect = $(this).attr('data-redirect') || '/';
                        $('.js-legal-disclaimer').addClass('!u-hidden').attr('aria-hidden', 'true');
                        $('body').css('overflow', 'auto');
                        setTimeout(() => window.location.href = redirect, 100);
                        return false;
                    });
                    console.log("✅ Emergency fallback handlers active");
                }
                catch (fallbackError) {
                    console.error("❌ Emergency fallback also failed:", fallbackError);
                }
            }
        }
        if (document.querySelector('.js-filter-category, .js-filter-category-all')) {
            const { filteredMediaLibraryResults } = yield import("@/modules/filtered-media-library");
            window["filteredMediaLibraryResults"] = filteredMediaLibraryResults;
            if (document.querySelector('.js-filtered-library-block')) {
                console.log("🔧 Initializing filtered library blocks");
                filteredMediaLibraryResults.blockInit();
            }
            else if (document.querySelector('.js-c-wide-result')) {
                console.log("📄 Filtered library page detected - module ready for template initialization");
            }
            loadedCount++;
            console.log("✅ Filtered media library module loaded and exposed to window");
        }
        if (document.querySelector('.js-contact-popup, .js-contact-image-outer_container, .js-wide-tabs, .wide-contacts, .wide-tabs')) {
            const [{ wideContactsPopUp, wideContactsFilter }, { wideTabs }] = yield Promise.all([
                import("@/modules/wide-contacts"),
                import("@/modules/wide-tabs")
            ]);
            wideContactsFilter.init();
            wideContactsPopUp.init();
            wideTabs.init();
            loadedCount++;
            console.log("✅ Wide contacts and tabs initialized");
        }
        const { injectCerosScript } = yield import("@/helpers/ceros-script");
        injectCerosScript();
    }
    catch (error) {
        handleModuleError('Specialized Features', error);
    }
    const duration = performance.now() - startTime;
    console.log(`✅ Phase 3 complete: ${loadedCount} specialized features loaded (${duration.toFixed(2)}ms)`);
});
$(document).ready(() => __awaiter(void 0, void 0, void 0, function* () {
    const initStartTime = performance.now();
    console.log("🚀 Starting application initialization sequence...");
    try {
        initializeCriticalFeatures();
        helloBar = HelloBar.init();
        enhancedStickyNav = EnhancedStickyNav.init();
        stickyNav = StickyNav.init();
        if (document.querySelector('#js-regulatory-banner-top, #js-regulatory-banner-bottom')) {
            const { default: ToastrHandler } = yield import("@/modules/Toastr/index");
            regulatoryBanner = ToastrHandler.init();
            if (regulatoryBanner) {
                console.log("✅ Regulatory banner system initialized");
            }
        }
        helloBar === null || helloBar === void 0 ? void 0 : helloBar.on("closed", () => {
            stickyNav === null || stickyNav === void 0 ? void 0 : stickyNav.adjustPosition();
        });
        header.applyStylesForHeaderNav();
        initializeProgressiveFeatures().then(() => {
            console.log("✅ Progressive features loading phase complete");
        }).catch(error => {
            handleModuleError('Progressive Features Phase', error);
        });
        setTimeout(() => {
            initializeSpecializedFeatures().then(() => {
                const totalDuration = performance.now() - startTime;
                console.log(`🎉 All application modules loaded successfully!`);
                console.log(`📊 Total initialization time: ${totalDuration.toFixed(2)}ms`);
                console.log(`📊 Modules successfully loaded: ${loadedModules.size}`);
                if (Object.keys(moduleLoadTimes).length > 0) {
                    console.log("⏱️ Module load performance:", moduleLoadTimes);
                }
            }).catch(error => {
                handleModuleError('Specialized Features Phase', error);
            });
        }, 100);
        const readyDuration = performance.now() - initStartTime;
        console.log(`✅ Application ready: Core initialization complete (${readyDuration.toFixed(2)}ms)`);
    }
    catch (error) {
        handleModuleError('Main Application Initialization', error, true);
    }
}));
$(window).on("load", () => __awaiter(void 0, void 0, void 0, function* () {
    console.log("🔄 Window load event: Finalizing initialization");
    try {
        scrollToURLFragment();
        header.handleSearchPosition();
        helloBar === null || helloBar === void 0 ? void 0 : helloBar.adjustPosition();
        stickyNav === null || stickyNav === void 0 ? void 0 : stickyNav.adjustPosition();
        regulatoryBanner === null || regulatoryBanner === void 0 ? void 0 : regulatoryBanner.adjustPosition();
        const clientPortalPerformanceData = window["ClientPortalPerformanceData"];
        if (clientPortalPerformanceData === null || clientPortalPerformanceData === void 0 ? void 0 : clientPortalPerformanceData.data) {
            const { ManualDatePicker } = yield import("@/modules/manual-date-picker");
            const accountElement = document.getElementById(clientPortalPerformanceData.accountId);
            const endOfMonthElement = document.getElementById(clientPortalPerformanceData.endOfMonthId);
            const endOfYearElement = document.getElementById(clientPortalPerformanceData.endOfYearId);
            if (accountElement && endOfMonthElement && endOfYearElement) {
                const performanceDatePicker = new ManualDatePicker({
                    availablePerformanceData: clientPortalPerformanceData.data,
                    accountElement,
                    endOfMonthElement,
                    endOfYearElement,
                });
                performanceDatePicker.init();
                console.log("✅ Client performance date picker initialized");
            }
        }
        console.log("✅ Window load event: All final initialization complete");
    }
    catch (error) {
        handleModuleError('Window Load Handler', error);
    }
}));
$(window).on("scroll", () => {
    header.applyStylesForHeaderNav();
    header.handleSearchPosition();
    helloBar === null || helloBar === void 0 ? void 0 : helloBar.adjustPosition();
    stickyNav === null || stickyNav === void 0 ? void 0 : stickyNav.adjustPosition();
    regulatoryBanner === null || regulatoryBanner === void 0 ? void 0 : regulatoryBanner.adjustPosition();
});
$(window).on("resize", () => {
    if (window["carousel"]) {
        window["carousel"].applyStyles();
    }
    if (window["flexSliderCarousel"]) {
        window["flexSliderCarousel"].positionPlayButton();
    }
});
$(window).on("orientationchange", () => {
    if (window["flexSliderCarousel"]) {
        window["flexSliderCarousel"].attatchPerspective();
    }
});
if (window.vimeoJSFileLocation != null) {
    $(document).ready(function () {
        if ($(".video-content-section").length) {
            $.getScript(window.vimeoJSFileLocation);
            console.log("✅ Vimeo player script loaded");
        }
    });
}
$(function () {
    if (window.location.href.indexOf(".pdf.") > -1) {
        window.location.href = window.location.href.slice(0, -1);
    }
    const perfEntries = performance.getEntriesByType("navigation");
    if (perfEntries[0].type === "back_forward") {
        console.log("🔄 Back/forward navigation detected - reloading for consistency");
        window.location.reload();
    }
});
if (process.env.NODE_ENV === 'development') {
    window.debugModules = {
        loadedModules: Array.from(loadedModules),
        loadTimes: moduleLoadTimes,
        forceReload: () => window.location.reload(),
        testModule: (name) => {
            console.log(`🔍 Testing module: ${name}`);
            console.log(`  Status: ${loadedModules.has(name) ? 'LOADED' : 'NOT LOADED'}`);
            console.log(`  Load time: ${moduleLoadTimes[name] || 'Not measured'}ms`);
        },
        getPerformanceSummary: () => {
            const totalModules = loadedModules.size;
            const avgLoadTime = Object.values(moduleLoadTimes).reduce((a, b) => a + b, 0) / Object.keys(moduleLoadTimes).length;
            const slowestModule = Object.entries(moduleLoadTimes).reduce((a, b) => a[1] > b[1] ? a : b);
            return {
                totalModules,
                avgLoadTime: avgLoadTime.toFixed(2),
                slowestModule: `${slowestModule[0]} (${slowestModule[1].toFixed(2)}ms)`,
                allTimes: moduleLoadTimes
            };
        },
        menuDebug: {
            getOpenMenus: () => menuHandler.getOpenMenus(),
            closeAllMenus: () => menuHandler.closeAllMenus(),
            refreshMenus: () => menuHandler.refresh(),
            findMenuByElement: (element) => menuHandler.findMenuByElement(element),
            isMenuOpen: (menuId) => menuHandler.isMenuOpen(menuId),
            getDiagnostics: () => menuHandler.getDiagnostics(),
            debugMenuPairing: (triggerSelector) => menuHandler.debugMenuPairing(triggerSelector)
        }
    };
    console.log("🛠️ Development tools available via window.debugModules");
}
console.log("🎯 Global module loader initialization sequence complete");
